import * as React from 'react';
import { memo, useContext, useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Fab,
  Checkbox,
  Button,
  makeStyles,
  TextField,
  CircularProgress,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteButton from '../common/DeleteButton';
import {
  DailyReportsExternalMember,
  DailyReportsExternalMemberModel,
} from '../../models/DailyReport/DailyReportsExternalMemberModel';

const useStyles = makeStyles(theme => ({
  tableContainer: {
    marginTop: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    height: '80vh',
    overflowY: 'auto',
  },
  headerCell: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 'bold',
  },
  addButton: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontWeight: 'bold',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  removeButton: {
    color: theme.palette.error.main,
    '&:hover': {
      backgroundColor: theme.palette.error.light,
    },
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  registerButton: {
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    },
  },
}));

interface DailyReportSelectExternalMemberProps {
  onSave: (e: DailyReportsExternalMember[]) => void;
}

const MemberContext = React.createContext<{
  members: DailyReportsExternalMember[];
  setMembers: React.Dispatch<React.SetStateAction<DailyReportsExternalMember[]>>;
}>({
  members: [],
  setMembers: () => {},
});

/**
 * 報告先(社外)メンバー設定テーブル
 * @param props
 * @returns
 */
const DailyReportSelectExternalMember = (props: DailyReportSelectExternalMemberProps) => {
  const classes = useStyles();
  const { onSave } = props;
  const [members, setMembers] = useState<DailyReportsExternalMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * 外部メンバーを取得する
   */
  const fetchUsers = async () => {
    let result = await DailyReportsExternalMemberModel.fetchAll();

    const empty = DailyReportsExternalMemberModel.empty();
    // ダミー行には一意のIDを生成（ここではindexを使用）
    setMembers([...result, { ...empty, id: result.length }]);

    setIsLoading(false);
  };

  const onRemove = (index: number) => {
    setMembers(members.filter((_, i) => i !== index));
  };

  const onAdd = () => {
    const empty = DailyReportsExternalMemberModel.empty();
    // ダミー行には一意のIDを生成（ここではindexを使用）
    setMembers([...members, { ...empty, id: members.length }]);
  };

  const handleSave = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // メールアドレスを半角に変換
    const normalizedMembers = members.map(member => ({
      ...member,
      email: member.email.replace(/[Ａ-Ｚａ-ｚ０-９]/g, s =>
        String.fromCharCode(s.charCodeAt(0) - 65248)
      ),
    }));

    // フィルタリング処理
    const filteredMembers = normalizedMembers.filter(
      member => member.name.trim() !== '' && emailRegex.test(member.email)
    );

    // メールアドレスの重複をチェック
    const seenEmails = new Set<string>();
    const uniqueFilteredMembers = filteredMembers.filter(member => {
      if (seenEmails.has(member.email)) {
        return false;
      } else {
        seenEmails.add(member.email);
        return true;
      }
    });

    onSave(uniqueFilteredMembers);
  };
  useEffect(() => {
    fetchUsers();
  }, []);

  if (isLoading) {
    return (
      <Box>
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Box style={{ textAlign: 'center', padding: 16 }}>
            <CircularProgress />
          </Box>
        </TableContainer>
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} className={classes.tableContainer}>
        {isLoading ? (
          <Box style={{ textAlign: 'center', padding: 16 }}>
            <CircularProgress />
          </Box>
        ) : null}

        <Table size="small">
          <TableHead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 999 }}>
            <TableRow>
              <TableCell className={classes.headerCell} style={{ width: 115 }} align="center">
                {`利用`}
              </TableCell>
              <TableCell className={classes.headerCell} style={{ minWidth: 150 }}>
                名前
              </TableCell>
              <TableCell className={classes.headerCell} style={{ minWidth: 200 }}>
                アドレス
              </TableCell>
              <TableCell className={classes.headerCell} style={{ width: 115 }}></TableCell>
            </TableRow>
          </TableHead>
          <MemberContext.Provider value={{ members, setMembers }}>
            <TableBody>
              {members.map((user, index) => (
                <ExternalMemberItem
                  key={`${user.id}_${index}`}
                  user={user}
                  index={index}
                  onRemove={onRemove}
                />
              ))}
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Fab
                    size="small"
                    style={{ background: '#a5fca5' }}
                    aria-label="add"
                    onClick={onAdd}
                  >
                    <AddIcon />
                  </Fab>
                </TableCell>
              </TableRow>
            </TableBody>
          </MemberContext.Provider>
        </Table>
      </TableContainer>
      <Box display="flex" justifyContent="center" mt={0}>
        <Button
          size="large"
          color="primary"
          variant="contained"
          className={classes.registerButton}
          onClick={handleSave}
        >
          確定
        </Button>
      </Box>
    </Box>
  );
};

interface ExternalMemberItemProps {
  user: DailyReportsExternalMember;
  index: number;
  onRemove: (index: number) => void;
}

const ExternalMemberItem = (props: ExternalMemberItemProps) => {
  const classes = useStyles();
  const { members, setMembers } = useContext(MemberContext);
  const { user, index, onRemove } = props;

  const handleEmailChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const newMembers = [...members];
    newMembers[index].email = e.target.value;
    setMembers(newMembers);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const newMembers = [...members];
    newMembers[index].name = e.target.value;
    setMembers(newMembers);
  };

  const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const newMembers = [...members];
    newMembers[index].is_send = checked ? 1 : 0;
    setMembers(newMembers);
  };

  return (
    <TableRow className={classes.tableRow}>
      <TableCell align="center">
        <Checkbox
          size="small"
          checked={user.is_send === 1}
          onChange={(event, checked) => handleCheckChange(event, checked)}
          color="primary"
        />
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          value={user.name}
          onChange={handleNameChange}
          variant="outlined"
          fullWidth
        />
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          value={user.email}
          onChange={handleEmailChange}
          variant="outlined"
          fullWidth
        />
      </TableCell>
      <TableCell>
        <DeleteButton onDelete={() => onRemove(index)}>削除</DeleteButton>
      </TableCell>
    </TableRow>
  );
};

export default DailyReportSelectExternalMember;
